<template>
    <aside v-if="sections.length > 1" class="guide-toc" aria-label="On this page">
        <div class="guide-toc__label">On this page</div>
        <ul class="guide-toc__list">
            <li v-for="s in sections" :key="s.id">
                <a
                    :href="`#${s.id}`"
                    :class="['guide-toc__link', { 'guide-toc__link--active': s.id === activeId }]"
                    @click="scrollTo($event, s.id)"
                >
                    {{ s.label }}
                </a>
            </li>
        </ul>
    </aside>
</template>

<script>
/**
 * In-page table of contents for a guide.
 *
 * Auto-discovers h2 elements inside the target container on mount (and whenever the
 * `targetKey` prop changes), slugifies their text content into anchor IDs, and renders
 * a scrollable list of links. An IntersectionObserver highlights whichever section is
 * currently visible so readers always know where they are in a long guide.
 *
 * Usage: place alongside the guide content, pointing at the content's root element:
 *   <div ref="content"><component :is="activeComponent" /></div>
 *   <GuideToc :target="$refs.content" :target-key="activeTopic" />
 */
export default {
    name: 'GuideToc',

    props: {
        // The DOM element whose descendant h2s should be indexed. Typically the element
        // wrapping the <component :is="activeComponent" /> that renders the current guide.
        target: { type: [Object, null], default: null },

        // A value that changes whenever a fresh guide is rendered. The TOC re-scans when
        // this changes. Typically bind to the parent's `activeTopic`.
        targetKey: { type: String, default: '' }
    },

    data() {
        return {
            sections: [],
            activeId: null,
            observer: null
        };
    },

    watch: {
        targetKey: {
            handler() {
                // Re-scan on next tick so the new guide has mounted into the DOM first.
                this.$nextTick(() => this.rescan());
            },
            immediate: false
        },
        target: {
            handler() {
                this.$nextTick(() => this.rescan());
            },
            immediate: false
        }
    },

    mounted() {
        this.$nextTick(() => this.rescan());
    },

    beforeUnmount() {
        this.teardownObserver();
    },

    methods: {
        rescan() {
            this.teardownObserver();
            if (!this.target) {
                this.sections = [];
                return;
            }

            const h2s = Array.from(this.target.querySelectorAll('h2'));
            this.sections = h2s.map((el) => {
                const label = (el.textContent || '').trim();
                const id = el.id || this.slugify(label);
                if (!el.id) el.id = id;
                // Give each heading a bit of scroll-margin-top so the in-page anchor
                // doesn't jump the heading flush against the viewport top.
                el.style.scrollMarginTop = '24px';
                return { id, label, el };
            });

            if (this.sections.length > 0) {
                this.activeId = this.sections[0].id;
                this.setupObserver();
            } else {
                this.activeId = null;
            }
        },

        setupObserver() {
            if (typeof IntersectionObserver === 'undefined') return;

            // Highlight the section closest to the top of the viewport that is currently visible.
            // The rootMargin treats anything below 35% of the viewport as "not yet active" so that
            // a section becomes the active one once its heading is comfortably above the fold.
            this.observer = new IntersectionObserver(
                (entries) => {
                    // Among entries that are currently intersecting, pick the one whose heading
                    // has the highest bounding-box top (i.e. the topmost visible heading).
                    const visible = entries
                        .filter((e) => e.isIntersecting)
                        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                    if (visible.length > 0) {
                        this.activeId = visible[0].target.id;
                    }
                },
                {
                    rootMargin: '0px 0px -65% 0px',
                    threshold: 0
                }
            );
            this.sections.forEach((s) => this.observer.observe(s.el));
        },

        teardownObserver() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        },

        scrollTo(event, id) {
            const el = document.getElementById(id);
            if (!el) return;
            event.preventDefault();
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Update hash without triggering another jump (scrollIntoView already did the jump).
            if (typeof history !== 'undefined' && history.replaceState) {
                history.replaceState(null, '', `#${id}`);
            }
            this.activeId = id;
        },

        slugify(text) {
            return text
                .toLowerCase()
                .replace(/&/g, 'and')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .slice(0, 64) || 'section';
        }
    }
};
</script>

<style scoped>
.guide-toc {
    width: 200px;
    flex-shrink: 0;
    position: sticky;
    /* 50px sticky header in _LayoutPublic + 24px breathing room */
    top: 74px;
    align-self: flex-start;
    /* Viewport minus header (50px) and top/bottom gaps (24px each) */
    max-height: calc(100vh - 98px);
    overflow-y: auto;
    padding: 4px 0 8px 16px;
    border-left: 1px solid #e2e8f0;
}

.guide-toc__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 8px;
}

.guide-toc__list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.guide-toc__link {
    display: block;
    padding: 5px 10px 5px 12px;
    border-left: 2px solid transparent;
    margin-left: -12px;
    font-size: 12.5px;
    line-height: 1.4;
    color: #64748b;
    text-decoration: none;
    transition: color 100ms ease-out, border-color 100ms ease-out;
}

.guide-toc__link:hover {
    color: #0f172a;
}

.guide-toc__link--active {
    color: #4F46E5;
    border-left-color: #6366F1;
    font-weight: 600;
}

/* TOC is polish - hide on narrow viewports where it would crowd the content. */
@media (max-width: 1200px) {
    .guide-toc {
        display: none;
    }
}
</style>
